import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from "react";

import { ThemeProvider } from "@material-ui/core/styles";
import CssBaseline from "@material-ui/core/CssBaseline";

import { getAppTheme } from "./theme";

const STORAGE_KEY = "app-theme-mode";

const readInitialMode = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved === "dark" || saved === "light" ? saved : "light";
};

const ThemeModeContext = createContext({ mode: "light", toggleMode: () => {} });

// Hook consumers use to read the current mode and flip it.
export const useThemeMode = () => useContext(ThemeModeContext);

// Wraps the app: owns the light/dark mode, persists the choice, and provides
// the matching MUI theme via ThemeProvider (+ CssBaseline for the body bg).
export default function ThemeModeProvider({ children }) {
  const [mode, setMode] = useState(readInitialMode);

  const toggleMode = useCallback(() => {
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  // Drive the CSS custom-property theme (assets/css/theme.css) off <html data-theme>.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
  }, [mode]);

  const theme = useMemo(() => getAppTheme(mode), [mode]);
  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
